import { graph } from "config/axios-context";
import { DocumentNode } from "graphql";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useAppContext from "utilities/hooks/useAppContext";
import { ComponentStateTypes } from "../ComponentState";
import Form, { FormPropsType } from "../form/Form";
import FormActions from "../form/partials/FormActions";
import Page, { PagePropsType } from "./Page";

export type PageFormProps<TQuery, TMutation, TMutationVariables> = Omit<PagePropsType, 'state' | 'children' | 'actions'> &
  Omit<FormPropsType, 'onSubmit' | 'children'> & {
    onSubmit: ((event: React.FormEvent<HTMLFormElement>) => Promise<void>) | {
        mutation: string | DocumentNode ,
        resolveMutationData: () => TMutationVariables,
        onSuccess?: (data?: TMutation) => void
    },
    load: {
        query: string | DocumentNode,
        resolveQueyData: (data?: TQuery) => void,
    }
}

type PageFormPropsType<TQuery, TMutation, TMutationVariables> = PageFormProps<TQuery, TMutation, TMutationVariables> & {
    children: React.ReactNode | React.ReactNode[]
}

const PageForm = <TQuery, TMutation, TMutationVariables> (props: PageFormPropsType<TQuery, TMutation, TMutationVariables>) => {
    const context = useAppContext();
    const params = useParams<{ id: string }>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>(params.id ? 'loading' : '');
    const [disableInputs, setDisableInputs] = useState<boolean>(false);

    const formConfig: Omit<FormPropsType, 'children'> = {
        name: props.name + '-form',
        onSubmit: e => onSubmit(e),
        onError: props.onError,
        onReset: props.onReset,
    }

    // #region load data
    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        try {
            const response = await graph<TQuery>({
                query: props.load.query,
                variables: {
                    id: Number(params.id) || 0
                }
            });

            if(response.errors) {
                throw response.errors;
            }

            setComponentState('');
            props.load.resolveQueyData(response.data);
        } catch (error: any) {
            context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }
    // #endregion load data

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        setDisableInputs(true);

        if(typeof props.onSubmit === 'function') {
            try {
                const sbm = props.onSubmit(event);
                if(sbm instanceof Promise) {
                    await sbm;
                }

                setDisableInputs(false);
            } catch (error: any) {
                setDisableInputs(false);
                throw error;
            }
            return;
        }

        const resolveMutationData = props.onSubmit.resolveMutationData;
        const mutation = props.onSubmit.mutation;
        const onSuccess = props.onSubmit.onSuccess;
        setDisableInputs(true);
        try {
            const variables = resolveMutationData();
            const response = await graph<TMutation, TMutationVariables>({
                query: mutation,
                variables
            });

            if(response.errors) {
                throw response.errors;
            }
            setDisableInputs(false);
            if(onSuccess) onSuccess(response.data);
            else context.navigate(-1);
        } catch (error: any) {
            context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
            setDisableInputs(false);
            throw error;
        }
    }

    return (
        <Page title={props.title} state={componentState}>
            <Form {...formConfig}>
                <>
                    {props.children}
                </>
                <FormActions disableInputs={disableInputs} />
            </Form>
        </Page>
    );
};

export default PageForm;
